import type React from 'react'
import type { ValueInputProps } from '@pushly/aqe'
import { createCountInWindowInput } from './count-in-window-input'
import { COUNT_IN_WINDOW_SURFACES } from './count-in-window-surfaces'
import { CustomPropertyInput } from './custom-property-input'
import { FrequencyWindowInput } from './frequency-window-input'
import { GeoPolygonInput } from './geo-polygon-input'
import { JourneyStatusInput } from './journey-status-input'
import { NotificationIdClickedInput } from './notification-id-clicked-input'
import { PromptPickerInput } from './prompt-picker-input'

/**
 * The controls for the count surfaces whose configuration never varies, built from the one table
 * their summaries also read so the two cannot come to describe different windows.
 */
const COUNT_IN_WINDOW_INPUTS: Record<string, React.ComponentType<ValueInputProps<any>>> = Object.fromEntries(
    Object.entries(COUNT_IN_WINDOW_SURFACES).map(([inputKey, config]) => [
        inputKey,
        createCountInWindowInput(config),
    ]),
)

/**
 * The value inputs segmentation supplies to the query builder, keyed the way the
 * builder resolves them: `field.inputKey ?? field.type`. The server catalog is
 * what names the key, so an entry here is only reachable once a registry field
 * declares it.
 *
 * Date fields have no entry either, and no `inputKey` in the catalog. The builder's own date
 * control reads which of a calendar, a count of units, a bounds pair or a named-window list a row
 * needs from the shape the engine reads the value as. Supplying a second date renderer here is what
 * gave one operator two different appearances: a custom entry is consulted before that shape
 * switch, so it received every operator and had to re-derive the routing — and its answer for
 * `between` disagreed with the builder's on the very next field.
 *
 * The `app-version` inputKey has no entry on purpose: its control is a plain semver
 * input the builder already supplies for `type: 'semver'`, so an unregistered key
 * falls through to that built-in — legacy likewise had no widget and fell through to
 * a text input. What app version needs beyond the control is the property collapse
 * between the engine's shared property and the catalog's channel-suffixed fields, and
 * a value renderer cannot rewrite the property it edits; that lives in the shell's
 * load/save seam (`../app-version`).
 */
export const SEGMENT_VALUE_INPUTS: Record<string, React.ComponentType<ValueInputProps<any>>> = {
    ...COUNT_IN_WINDOW_INPUTS,
    'frequency-window': FrequencyWindowInput,
    'prompt-picker': PromptPickerInput,
    'notification-id-clicked': NotificationIdClickedInput,
    'custom-property': CustomPropertyInput,
    'geo-polygon': GeoPolygonInput,
    'journey-status': JourneyStatusInput,
}

export { CustomPropertyInput } from './custom-property-input'
export { GeoPolygonInput } from './geo-polygon-input'
export { attachPolygonMap } from './geo-polygon-map'
export type { PolygonMapConfig, PolygonMapHandle, PolygonPoint } from './geo-polygon-map'
export { createCountInWindowInput, CountInWindowInput, WINDOW_LABEL } from './count-in-window-input'
export type { CountInWindowConfig, CountInWindowExpression, CountInWindowWindowOption } from './count-in-window-input'
export { COUNT_IN_WINDOW_SURFACES } from './count-in-window-surfaces'
export { frequencyWindowConfig, FrequencyWindowInput } from './frequency-window-input'
export { describeNotification, NotificationIdClickedInput } from './notification-id-clicked-input'
export {
    createFrequencyWindowSummary,
    createJourneyStatusSummary,
    createNotificationIdClickedSummary,
    createPromptPickerSummary,
    summariseCountInWindow,
    useSegmentValueSummaries,
} from './summaries'
export { PromptPickerInput } from './prompt-picker-input'
export {
    JOURNEY_LABELS,
    JOURNEY_OPERATORS,
    JOURNEY_SELECTION_TYPES,
    JourneyStatusInput,
} from './journey-status-input'
export {
    buildJourneyTree,
    deriveJourneyState,
    JOURNEY_STATUSES,
    JOURNEY_TRIGGERS,
} from './journey-status-tree'
export type { JourneySelectionType, JourneyStatusState } from './journey-status-tree'
export { SEGMENT_VALUE_VALIDATORS } from './validators'
export { SegmentValueInputContext, useSegmentValueInputContext } from './segment-value-input-context'
export type { JourneyOption, PromptOption, SegmentValueInputContextValue } from './segment-value-input-context'
export { createPageTagSource } from './page-tag-source'
export type { PageTagPage, PageTagReadRequest, PageTagScope, PageTagSource } from './page-tag-source'
export { createNotificationSource } from './notification-source'
export type { NotificationOption, NotificationReader, NotificationSource } from './notification-source'
